import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Save, Eye, EyeOff, ArrowUp, ArrowDown, ExternalLink } from 'lucide-react';
import { adminApi, publicApi } from '../../api';
import { PageHeader, Table, Modal, Field, Input, Select, ImageUpload } from './AdminUI';
import Sparkline from '../../components/Sparkline';

/** Lazy-loading cell — fetches 14-day banner stats when the row renders */
function BannerStatsCell({ bannerId }) {
  const [series, setSeries] = useState(null);
  useEffect(() => {
    let alive = true;
    adminApi.bannerStats(bannerId, 14)
      .then(r => { if (alive) setSeries(r.series); })
      .catch(() => { if (alive) setSeries([]); });
    return () => { alive = false; };
  }, [bannerId]);
  if (series === null) return <span className="text-xs text-gray-400">…</span>;
  return <Sparkline data={series} />;
}

const EMPTY = {
  title: '', image: '', link_url: '', city_slug: '', city_slugs: [], is_active: true, order: 0, start_date: '', end_date: ''
};

export default function AdminBanners() {
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => adminApi.banners().then(setItems);
  useEffect(() => {
    load();
    publicApi.cities().then(setCities);
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const citySlugs = editing.city_slugs || [];
      const payload = {
        title: editing.title || '',
        image: editing.image || '',
        link_url: editing.link_url || null,
        // Primary city_slug: first from multi-select, or empty string = tutte
        city_slug: citySlugs.length === 1 ? citySlugs[0] : (citySlugs.length > 1 ? citySlugs[0] : null),
        city_slugs: citySlugs,
        is_active: !!editing.is_active,
        order: Number(editing.order) || 0,
        // Force start = start-of-day, end = end-of-day (avoids the "banner scaduto lo stesso giorno" bug)
        start_date: editing.start_date ? _toStartOfDay(editing.start_date) : null,
        end_date: editing.end_date ? _toEndOfDay(editing.end_date) : null
      };
      if (!payload.image) { alert('Immagine banner obbligatoria'); return; }
      if (editing.id) await adminApi.updateBanner(editing.id, payload);
      else await adminApi.createBanner(payload);
      setEditing(null); load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore salvataggio');
    }
  };

  function _toStartOfDay(v) {
    const d = new Date(v);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
  function _toEndOfDay(v) {
    const d = new Date(v);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  }

  const del = async (row) => {
    if (!window.confirm(`Eliminare il banner "${row.title || row.id}"?`)) return;
    await adminApi.deleteBanner(row.id); load();
  };

  const toggleActive = async (row) => {
    await adminApi.updateBanner(row.id, { ...row, is_active: !row.is_active });
    load();
  };

  const move = async (row, dir) => {
    const idx = items.findIndex(b => b.id === row.id);
    const target = items[idx + dir];
    if (!target) return;
    const a = row.order || 0, b = target.order || 0;
    const orderA = a === b ? idx + dir : b;
    const orderB = a === b ? idx : a;
    await Promise.all([
      adminApi.updateBanner(row.id, { ...row, order: orderA }),
      adminApi.updateBanner(target.id, { ...target, order: orderB })
    ]);
    load();
  };

  return (
    <div>
      <PageHeader
        title="Banner Pubblicitari"
        subtitle={`${items.length} banner configurati — vengono mostrati nelle pagine città sotto la sezione meteo`}
        action={
          <button
            onClick={() => setEditing({...EMPTY, order: items.length})}
            className="btn-primary flex items-center gap-2"
            data-testid="admin-banner-new-btn"
          >
            <Plus size={16}/> Nuovo Banner
          </button>
        }
      />

      <Table
        columns={[
          { key: 'order', label: 'Ordine', render: (r, i) => (
            <div className="flex flex-col items-center gap-0.5">
              <button onClick={() => move(r, -1)} disabled={i === 0} className="p-0.5 hover:bg-gray-100 rounded text-gray-600 disabled:opacity-30"><ArrowUp size={14}/></button>
              <span className="text-xs text-gray-500 font-mono">{r.order ?? 0}</span>
              <button onClick={() => move(r, +1)} disabled={i === items.length - 1} className="p-0.5 hover:bg-gray-100 rounded text-gray-600 disabled:opacity-30"><ArrowDown size={14}/></button>
            </div>
          )},
          { key: 'image', label: '', render: r => r.image ? (
              <img
                src={r.image.startsWith('http') ? r.image : `${process.env.REACT_APP_BACKEND_URL}${r.image}`}
                alt="" className="w-24 h-12 object-cover rounded"
              />
            ) : '—'
          },
          { key: 'title', label: 'Titolo', render: r => <span className="font-semibold">{r.title || <em className="text-gray-400">Senza titolo</em>}</span> },
          { key: 'link_url', label: 'Link', render: r => r.link_url ? (
              <a href={r.link_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1 text-xs">
                {r.link_url.substring(0, 30)}{r.link_url.length > 30 ? '…' : ''} <ExternalLink size={11}/>
              </a>
            ) : <span className="text-gray-400 text-xs">—</span>
          },
          { key: 'city_slug', label: 'Città', render: r => {
              const list = (r.city_slugs && r.city_slugs.length) ? r.city_slugs : (r.city_slug ? [r.city_slug] : []);
              if (list.length === 0) return <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">Tutte</span>;
              return (
                <div className="flex flex-wrap gap-1">
                  {list.map(s => <span key={s} className="px-2 py-0.5 rounded bg-gray-100 text-xs">{s}</span>)}
                </div>
              );
            }
          },
          { key: 'is_active', label: 'Stato', render: r => (
            <button onClick={() => toggleActive(r)} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${r.is_active ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
              {r.is_active ? <><Eye size={12}/> Attivo</> : <><EyeOff size={12}/> Disattivo</>}
            </button>
          )},
          { key: 'date_range', label: 'Programmazione', render: r => {
              const now = new Date();
              const start = r.start_date ? new Date(r.start_date) : null;
              const end = r.end_date ? new Date(r.end_date) : null;
              const expired = end && end < now;
              const upcoming = start && start > now;
              return (
                <div className="text-xs text-gray-600">
                  <div>
                    {start ? start.toLocaleDateString('it-IT') : '—'}
                    {' → '}
                    {end ? end.toLocaleDateString('it-IT') : 'sempre'}
                  </div>
                  {expired && (
                    <div className="mt-1 inline-flex items-center gap-1">
                      <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-bold uppercase" data-testid={`banner-expired-${r.id}`}>⏱ Scaduto</span>
                      <button
                        onClick={async () => {
                          const nowD = new Date();
                          const newEnd = new Date(nowD.getFullYear(), nowD.getMonth(), nowD.getDate() + 30, 23, 59, 59, 999);
                          await adminApi.updateBanner(r.id, {
                            title: r.title || '',
                            image: r.image,
                            link_url: r.link_url || null,
                            city_slug: r.city_slug || null,
                            city_slugs: r.city_slugs || [],
                            is_active: r.is_active !== false,
                            order: r.order || 0,
                            start_date: r.start_date || null,
                            end_date: newEnd.toISOString(),
                          });
                          load();
                        }}
                        data-testid={`banner-extend-${r.id}`}
                        className="px-2 py-0.5 rounded bg-blue-500 hover:bg-blue-600 text-white text-[10px] font-semibold"
                      >+30 giorni</button>
                    </div>
                  )}
                  {upcoming && (
                    <span className="mt-1 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold uppercase">📅 Futuro</span>
                  )}
                </div>
              );
            }
          },
          { key: 'stats', label: 'Trend 14 giorni', render: r => <BannerStatsCell bannerId={r.id} /> }
        ]}
        rows={items}
        actions={row => (
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing({...row})} className="p-1.5 hover:bg-gray-100 rounded text-blue-600" data-testid={`admin-banner-edit-${row.id}`}><Edit2 size={15}/></button>
            <button onClick={() => del(row)} className="p-1.5 hover:bg-gray-100 rounded text-red-600" data-testid={`admin-banner-delete-${row.id}`}><Trash2 size={15}/></button>
          </div>
        )}
      />

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Modifica banner' : 'Nuovo banner'} maxWidth="max-w-2xl">
        {editing && (
          <form onSubmit={save}>
            <Field label="Immagine banner (obbligatoria)" hint="Formato consigliato: 1600×400 px, formato JPG/PNG/WEBP">
              <ImageUpload value={editing.image || ''} onChange={v => setEditing({...editing, image: v})} />
            </Field>
            <Field label="Titolo (opzionale, mostrato in overlay)">
              <Input value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})} placeholder="Es. Aperitivo al tramonto" />
            </Field>
            <Field label="Link destinazione (dove porta il click)">
              <Input value={editing.link_url || ''} onChange={e => setEditing({...editing, link_url: e.target.value})} placeholder="https://..." />
            </Field>
            <Field label="Città in cui mostrare il banner" hint="Nessuna selezione = tutte le città. Seleziona una o più città per limitare la visibilità.">
              <div className="flex flex-wrap gap-2 pt-1" data-testid="banner-city-selector">
                <button
                  type="button"
                  onClick={() => setEditing({...editing, city_slugs: []})}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    (editing.city_slugs || []).length === 0
                      ? 'bg-[color:var(--yup-primary)] border-[color:var(--yup-primary)] text-white'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
                  data-testid="banner-city-all"
                >
                  🌐 Tutte le città
                </button>
                {cities.map(c => {
                  const active = (editing.city_slugs || []).includes(c.slug);
                  return (
                    <button
                      type="button"
                      key={c.slug}
                      onClick={() => setEditing({
                        ...editing,
                        city_slugs: active
                          ? (editing.city_slugs || []).filter(s => s !== c.slug)
                          : [...(editing.city_slugs || []), c.slug]
                      })}
                      data-testid={`banner-city-${c.slug}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                        active
                          ? 'bg-[color:var(--yup-primary)] border-[color:var(--yup-primary)] text-white'
                          : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {active && '✓ '}{c.name}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Ordine">
              <Input type="number" value={editing.order} onChange={e => setEditing({...editing, order: e.target.value})} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Data inizio (opzionale)" hint="Inizia dalla mezzanotte del giorno selezionato">
                <Input
                  type="date"
                  value={editing.start_date ? String(editing.start_date).slice(0, 10) : ''}
                  onChange={e => setEditing({...editing, start_date: e.target.value || ''})}
                />
              </Field>
              <Field label="Data fine (opzionale)" hint="Include l'intera giornata del giorno selezionato">
                <Input
                  type="date"
                  value={editing.end_date ? String(editing.end_date).slice(0, 10) : ''}
                  onChange={e => setEditing({...editing, end_date: e.target.value || ''})}
                />
              </Field>
            </div>
            <Field label="Attivo">
              <label className="flex items-center gap-2 pt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.is_active}
                  onChange={e => setEditing({...editing, is_active: e.target.checked})}
                  className="h-4 w-4 accent-[color:var(--yup-primary)]"
                />
                Banner attivo (visibile ai visitatori)
              </label>
            </Field>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Annulla</button>
              <button type="submit" className="btn-primary flex items-center gap-2" data-testid="admin-banner-save-btn"><Save size={16}/> Salva</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
